import {inject, Injectable} from '@angular/core';
import {forkJoin, Observable, of} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';

import {DesignCriteria, DirectusService} from '@frontend/directus';
import {
  DesignCriteriaControllerService,
  DesignCriteriaResponse,
  UpsertDesignCriteriaRequest,
} from '@frontend/shared-api';
import {FixedValueService} from '../../../../../../shared/services/fixed-value.service';

interface LoadResult {
  criteria: DesignCriteria[];
  selections: DesignCriteriaResponse[];
}

@Injectable({providedIn: 'root'})
export class DesignCriteriaService {
  private readonly COLLECTION_NAME = 'design_criteria';

  private readonly directusService = inject(DirectusService);
  private readonly fixedValueService = inject(FixedValueService);
  private readonly api = inject(DesignCriteriaControllerService);

  getDesignCriteria(projectId: number): Observable<LoadResult> {
    return this.fixedValueService
      .loadOrInitializeFixedValues(projectId, this.COLLECTION_NAME, () =>
        this.directusService.loadDesignCriteria(),
      )
      .pipe(
        switchMap((storedIds) =>
          forkJoin({
            storedIds: of(storedIds),
            allCriteria: this.directusService.loadDesignCriteria(),
            selections: this.api.getDesignCriteriaByProjectId({projectId}),
          }),
        ),
        map(({storedIds, allCriteria, selections}) => ({
          criteria: this.filterCriteriaByStoredIds(allCriteria, storedIds),
          selections,
        })),
      );
  }

  private filterCriteriaByStoredIds(
    allCriteria: DesignCriteria[],
    storedIds: string[],
  ): DesignCriteria[] {
    if (storedIds.length === 0) {
      return allCriteria;
    }
    return allCriteria.filter((criteria) => storedIds.includes(criteria.id));
  }

  upsertDesignCriteria(
    projectId: number,
    request: UpsertDesignCriteriaRequest,
  ): Observable<DesignCriteriaResponse> {
    return this.api.upsertDesignCriteria({projectId, upsertDesignCriteriaRequest: request});
  }
}
